import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthUserContext } from '../../../components/Session';
import { documentsList, documentsDelete, documentsGet, templatesGet } from '../utils/templatesApi';
import { exportToPdf } from '../utils/exportPdf';
import { exportToWord } from '../utils/exportWord';
import PreviewPane from '../components/PreviewPane';
import '../templates.css';

const DocumentsPage = ({ firebase }) => {
    const authUser = useContext(AuthUserContext);
    const history = useHistory();

    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [regenerating, setRegenerating] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const previewRef = useRef(null);

    const loadDocuments = useCallback(async () => {
        if (!firebase || !authUser?.uid) return;

        setLoading(true);
        try {
            const response = await documentsList(firebase);
            setDocuments(response?.items || []);
        } catch (err) {
            console.error('Failed to load documents:', err);
        } finally {
            setLoading(false);
        }
    }, [firebase, authUser?.uid]);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    const handleDelete = async (documentId) => {
        if (!window.confirm('Are you sure you want to delete this document? This will permanently remove it from storage.')) return;

        // Show loading state
        const originalDocs = [...documents];
        setDocuments((prev) => prev.filter((d) => d.id !== documentId));

        try {
            await documentsDelete(firebase, documentId);
            // Success - doc already removed from state
        } catch (err) {
            console.error('Failed to delete document:', err);
            // Restore document on error
            setDocuments(originalDocs);
            alert('Failed to delete document. Please try again.');
        }
    };

    const handleDownload = async (doc, format = 'pdf') => {
        if (!firebase) return;

        setRegenerating(doc.id);
        try {
            // doc already has templateId and templateTitle from the list
            const templateId = doc.templateId;
            if (!templateId) {
                alert('Template ID not found for this document.');
                return;
            }

            // Get the template
            const template = await templatesGet(firebase, templateId);

            // Get full document data for values
            let values = {};
            try {
                const docData = await documentsGet(firebase, doc.id);
                values = docData.values || {};
            } catch (err) {
                console.warn('Could not fetch document values, using empty:', err);
            }

            const filename = doc.templateTitle || 'Document';

            if (format === 'word') {
                // Export to Word directly
                await exportToWord(template, values, filename);
            } else {
                // Export to PDF - need to render preview first
                setPreviewData({ template, values });

                // Wait for render
                await new Promise((resolve) => setTimeout(resolve, 200));

                // Generate PDF
                if (previewRef.current) {
                    await exportToPdf(previewRef.current, filename);
                }
            }
        } catch (err) {
            console.error('Failed to download document:', err);
            alert('Failed to download document. Please try again.');
        } finally {
            setRegenerating(null);
            setPreviewData(null);
        }
    };

    const filteredDocuments = searchQuery.trim()
        ? documents.filter((d) =>
            d.templateTitle?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : documents;

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };


    return (
        <div className="templates-page">
            <div className="templates-header">
                <h1>Generated Documents</h1>
                <p>View and manage documents you've generated from templates.</p>
            </div>

            {/* Controls */}
            <div className="templates-controls" style={{ marginBottom: '20px' }}>
                <button
                    className="editor-back-btn"
                    onClick={() => history.push('/franchise-dashboard/templates')}
                >
                    ← Back to Templates
                </button>

                <div className="templates-search" style={{ marginLeft: 'auto' }}>
                    <input
                        type="text"
                        placeholder="Search documents..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Documents Table */}
            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(248,248,248,0.5)' }}>
                    Loading documents...
                </div>
            ) : filteredDocuments.length === 0 ? (
                <div className="documents-empty">
                    <svg
                        width="64"
                        height="64"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        style={{ opacity: 0.3, marginBottom: '16px' }}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    <h3>No documents yet</h3>
                    <p>Documents you generate from templates will appear here.</p>
                </div>
            ) : (
                <table className="documents-table">
                    <thead>
                        <tr>
                            <th>Template</th>
                            <th>Created</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDocuments.map((doc) => (
                            <tr key={doc.id}>
                                <td>
                                    <strong>{doc.templateTitle || 'Untitled'}</strong>
                                </td>
                                <td>{formatDate(doc.createdAt)}</td>
                                <td>
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            background: 'rgba(34, 197, 94, 0.15)',
                                            color: '#22c55e',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {doc.status || 'Generated'}
                                    </span>
                                </td>
                                <td>
                                    <div className="doc-actions">
                                        <button
                                            className="doc-action-btn doc-action-btn-download"
                                            onClick={() => handleDownload(doc, 'pdf')}
                                            disabled={regenerating === doc.id}
                                            title="Download as PDF"
                                        >
                                            {regenerating === doc.id ? '...' : (
                                                <>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                        <line x1="16" y1="13" x2="8" y2="13" />
                                                        <line x1="16" y1="17" x2="8" y2="17" />
                                                        <polyline points="10 9 9 9 8 9" />
                                                    </svg>
                                                    PDF
                                                </>
                                            )}
                                        </button>
                                        <button
                                            className="doc-action-btn doc-action-btn-download"
                                            onClick={() => handleDownload(doc, 'word')}
                                            disabled={regenerating === doc.id}
                                            title="Download as Word"
                                        >
                                            {regenerating === doc.id ? '...' : (
                                                <>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                        <path d="M9 15l2 2 4-4" />
                                                    </svg>
                                                    Word
                                                </>
                                            )}
                                        </button>
                                        <button
                                            className="doc-action-btn doc-action-btn-delete"
                                            onClick={() => handleDelete(doc.id)}
                                            title="Delete document"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                <line x1="10" y1="11" x2="10" y2="17" />
                                                <line x1="14" y1="11" x2="14" y2="17" />
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Hidden preview for PDF generation */}
            {previewData && (
                <div
                    ref={previewRef}
                    style={{
                        position: 'absolute',
                        left: '-9999px',
                        top: 0,
                        width: '210mm',
                        background: '#ffffff',
                    }}
                >
                    <PreviewPane template={previewData.template} values={previewData.values} />
                </div>
            )}
        </div>
    );
};

export default DocumentsPage;

